import { StorageRootDto, UpdateResultDto } from 'UvExplorerServer.Dtos.TS';
import { AccountType, UserClass } from 'UvExplorerServer.Dtos.TS/Auth';
import { ValueEncoder, HashSet, ObjUtils } from "DiscoveryEngine.TS/Utils";
import { ObjSerUtils } from "DiscoveryEngine.TS/Serialization";
import { Privileges } from "./Privileges";


export class GroupDto extends StorageRootDto {

	private static readonly Default_AccountType = AccountType.Native;
	private static readonly Default_IsActive = true;
	private static readonly Default_UserClass = UserClass.Regular;
	private static readonly Default_EnableUserAutoProvisioning = false;
	private static readonly Default_AllowSamlSingleSignOn = false;

	public AccountType: AccountType;
	public Name: string;
	public Description: string;
	public MemberGroupGuids: HashSet<string>;
	public MemberUserGuids: HashSet<string>;
	public IsActive: boolean;
	public DirectoryId: string;
	public DirectoryId_2: string;
	public DirectoryDomainName: string;
	public UserClass: UserClass;
	public Privileges: Privileges;
	public EnableUserAutoProvisioning: boolean;
	public AllowSamlSingleSignOn: boolean;

	constructor(accountType: AccountType = GroupDto.Default_AccountType) {
		super();

		this.AccountType = accountType;
		this.Name = "";
		this.Description = "";
		this.MemberGroupGuids = new HashSet<string>();
		this.MemberUserGuids = new HashSet<string>();
		this.IsActive = GroupDto.Default_IsActive;
		this.DirectoryId = "";
		this.DirectoryId_2 = "";
		this.DirectoryDomainName = "";
		this.UserClass = GroupDto.Default_UserClass;
		this.Privileges = new Privileges();
		this.EnableUserAutoProvisioning = GroupDto.Default_EnableUserAutoProvisioning;
		this.AllowSamlSingleSignOn = GroupDto.Default_AllowSamlSingleSignOn;
	}

	public get HasName(): boolean {
		return !!this.Name;
	}

	public get HasDescription(): boolean {
		return !!this.Description;
	}

	public get HasMemberGroupGuids(): boolean {
		return (!!this.MemberGroupGuids && this.MemberGroupGuids.Count > 0);
	}

	public get HasMemberUserGuids(): boolean {
		return (!!this.MemberUserGuids && this.MemberUserGuids.Count > 0);
	}

	public get HasDirectoryId(): boolean {
		return !!this.DirectoryId;
	}

	public get HasDirectoryId_2(): boolean {
		return !!this.DirectoryId_2;
	}

	public get HasDirectoryDomainName(): boolean {
		return !!this.DirectoryDomainName;
	}

	public get HasPrivileges(): boolean {
		return (!!this.Privileges && this.Privileges.HasEntries);
	}

	public CopyProps(obj: UpdateResultDto, isCreate: boolean = true) {
		super.CopyProps(obj, isCreate);
	}

	public Equals(obj: any): boolean {
		if (!super.Equals(obj))
			return false;

		if (!(obj instanceof GroupDto))
			return false;
		let other: GroupDto = (obj as GroupDto);

		return (ObjUtils.Equals(this.AccountType, other.AccountType) &&
			ObjUtils.Equals(this.Name, other.Name) &&
			ObjUtils.Equals(this.Description, other.Description) &&
			ObjUtils.SetEquals(this.MemberGroupGuids, other.MemberGroupGuids) &&
			ObjUtils.SetEquals(this.MemberUserGuids, other.MemberUserGuids) &&
			ObjUtils.Equals(this.IsActive, other.IsActive) &&
			ObjUtils.Equals(this.DirectoryId, other.DirectoryId) &&
			ObjUtils.Equals(this.DirectoryId_2, other.DirectoryId_2) &&
			ObjUtils.Equals(this.DirectoryDomainName, other.DirectoryDomainName) &&
			ObjUtils.Equals(this.UserClass, other.UserClass) &&
			ObjUtils.Equals(this.Privileges, other.Privileges) &&
			ObjUtils.Equals(this.EnableUserAutoProvisioning, other.EnableUserAutoProvisioning) &&
			ObjUtils.Equals(this.AllowSamlSingleSignOn, other.AllowSamlSingleSignOn));
	}

	public Write(obj: any, context: any): void {

		obj.group = {};
		obj = obj.group;

		super.Write(obj, context);

		if (this.AccountType !== GroupDto.Default_AccountType)
			obj.account_type = AccountType[this.AccountType];

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasDescription)
			obj.description = this.Description;

		if (this.HasMemberGroupGuids) {
			obj.member_group_guids = [];
			ObjSerUtils.WriteStringValueCollection(obj.member_group_guids, this.MemberGroupGuids, context);
		}

		if (this.HasMemberUserGuids) {
			obj.member_user_guids = [];
			ObjSerUtils.WriteStringValueCollection(obj.member_user_guids, this.MemberUserGuids, context);
		}

		obj.is_active = ValueEncoder.EncodeBoolean(this.IsActive);

		if (this.HasDirectoryId)
			obj.directory_id = this.DirectoryId;

		if (this.HasDirectoryId_2)
			obj.directory_id_2 = this.DirectoryId_2;

		if (this.HasDirectoryDomainName)
			obj.directory_domain_name = this.DirectoryDomainName;

		if (this.UserClass !== GroupDto.Default_UserClass)
			obj.user_class = UserClass[this.UserClass];

		if (this.HasPrivileges)
			this.Privileges.Write(obj, context);

		obj.enable_user_auto_provisioning = ValueEncoder.EncodeBoolean(this.EnableUserAutoProvisioning);
		obj.allow_saml_single_sign_on = ValueEncoder.EncodeBoolean(this.AllowSamlSingleSignOn);
	}

	public Read(obj: any, context: any): void {

		if (!obj.group)
			throw new Error();
		obj = obj.group;

		super.Read(obj, context);

		if (obj.account_type !== undefined)
			this.AccountType = AccountType[<string>obj.account_type];

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.description !== undefined)
			this.Description = obj.description;

		if (obj.member_group_guids !== undefined) {
			this.MemberGroupGuids.Clear();
			ObjSerUtils.ReadStringValueCollection(obj.member_group_guids, this.MemberGroupGuids, context);
		}

		if (obj.member_user_guids !== undefined) {
			this.MemberUserGuids.Clear();
			ObjSerUtils.ReadStringValueCollection(obj.member_user_guids, this.MemberUserGuids, context);
		}

		if (obj.is_active !== undefined)
			this.IsActive = ValueEncoder.DecodeBoolean(obj.is_active);

		if (obj.directory_id !== undefined)
			this.DirectoryId = obj.directory_id;

		if (obj.directory_id_2 !== undefined)
			this.DirectoryId_2 = obj.directory_id_2;

		if (obj.directory_domain_name !== undefined)
			this.DirectoryDomainName = obj.directory_domain_name;

		if (obj.user_class !== undefined)
			this.UserClass = UserClass[<string>obj.user_class];

		if (obj.privileges !== undefined) {
			this.Privileges.Read(obj, context);
		}

		if (obj.enable_user_auto_provisioning !== undefined) {
			this.EnableUserAutoProvisioning = ValueEncoder.DecodeBoolean(obj.enable_user_auto_provisioning);
		}

		if (obj.allow_saml_single_sign_on !== undefined) {
			this.AllowSamlSingleSignOn = ValueEncoder.DecodeBoolean(obj.allow_saml_single_sign_on);
		}
	}

}